import React from "react";
import {
  Alert,
  Box,
  ColumnLayout,
  Container,
  CopyToClipboard,
  Header,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from "@cloudscape-design/components";
import { FailedExecution } from "../../services/StepFunctionFailuresService";
import { WorkflowStatus } from "../../services";
import { JsonViewer } from "../common";
import { AwsUrlBuilder, DateFormatter, JsonFormatter, ResultFormatter, StatusMapper } from "../../utils";
import { LABELS, SFN_FAILURES_LABELS } from "../../constants";

interface FailureDetailModalProps {
  execution: FailedExecution | null;
  visible: boolean;
  onDismiss: () => void;
}

const CAUSE_MAX_HEIGHT = 400;

const CauseView: React.FC<{ cause: string }> = ({ cause }) => {
  const causeJson = JsonFormatter.parse(cause);
  if (causeJson) {
    return <JsonViewer data={causeJson} emptyMessage={LABELS.EMPTY_VALUE} maxHeight={CAUSE_MAX_HEIGHT} />;
  }
  return (
    <div style={{ maxHeight: CAUSE_MAX_HEIGHT, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      <Box variant="code" fontSize="body-s">
        {cause}
      </Box>
    </div>
  );
};

export const FailureDetailModal: React.FC<FailureDetailModalProps> = ({ execution, visible, onDismiss }) => {
  if (!execution) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="large"
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Link external href={AwsUrlBuilder.buildStepFunctionsUrl(execution.executionArn)}>
                {LABELS.OPEN_IN_CONSOLE}
              </Link>
              <CopyToClipboard
                copyButtonText={SFN_FAILURES_LABELS.COPY}
                copySuccessText={SFN_FAILURES_LABELS.COPIED}
                copyErrorText={SFN_FAILURES_LABELS.COPY_FAILED}
                textToCopy={ResultFormatter.formatFailedExecution(execution)}
              />
            </SpaceBetween>
          }
        >
          {execution.name}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h3">{LABELS.EXECUTION_DETAILS}</Header>}>
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">{SFN_FAILURES_LABELS.STATUS}</Box>
              <StatusIndicator type={StatusMapper.mapWorkflowStatus(execution.status as WorkflowStatus)}>
                {execution.status}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">{SFN_FAILURES_LABELS.STARTED}</Box>
              <Box>{DateFormatter.formatDate(execution.startDate, LABELS.EMPTY_VALUE, "utc")}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">{SFN_FAILURES_LABELS.STOPPED}</Box>
              <Box>{DateFormatter.formatDate(execution.stopDate, LABELS.EMPTY_VALUE, "utc")}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">{LABELS.DURATION}</Box>
              <Box>{DateFormatter.formatDuration(execution.startDate, execution.stopDate, LABELS.EMPTY_VALUE)}</Box>
            </div>
          </ColumnLayout>
        </Container>

        <Container header={<Header variant="h3">{LABELS.EXECUTION_ARN}</Header>}>
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Box variant="code" fontSize="body-s">
              {execution.executionArn}
            </Box>
            <CopyToClipboard
              copyButtonAriaLabel={SFN_FAILURES_LABELS.COPY_ROW_ARIA_LABEL}
              copySuccessText={SFN_FAILURES_LABELS.COPIED}
              copyErrorText={SFN_FAILURES_LABELS.COPY_FAILED}
              textToCopy={execution.executionArn}
            />
          </SpaceBetween>
        </Container>

        {execution.workflowError && (
          <Alert type="error" header={LABELS.EXECUTION_ERROR}>
            <SpaceBetween size="s">
              <div>
                <Box variant="awsui-key-label">{SFN_FAILURES_LABELS.ERROR}</Box>
                <Box>{execution.workflowError.error || LABELS.EMPTY_VALUE}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">{SFN_FAILURES_LABELS.CAUSE}</Box>
                {execution.workflowError.cause ? <CauseView cause={execution.workflowError.cause} /> : LABELS.EMPTY_VALUE}
              </div>
            </SpaceBetween>
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
};
